/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */
import React, { useEffect, useState } from "react";
import { Button, FormLabel, Grid, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../utils/page-title/PageTitle";
import UploadProfilePic from "../../utils/upload-profile-pic/UploadProfilePic";
import "./HoneywellUserInfo.scss";
import { ConfigurationVariables } from "../../config";
import useFetch from "../../hooks/useFetch";
import Loader from "../Loader/Loader";
import { IErrorProps } from "../../models";
import ToastBarForError from "../SharedComponents/ToastBarForError";
import { getAccessToken } from "../../utils";
import classes from "../UserManagement/UserInformation.module.css";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import UserDisplayDetails from "../../utils/UserInfo/UserInfoBox";
import editLogo from "../../images/DefaultProfilePic.png";
/**
 *
 * @summery to display honeywell user information
 */
const HoneywellUserInfo = () => {
  const location = useLocation();
  const { UserInfo } = location?.state;
  const roleNameList = UserInfo?.roles?.map((role: any) => role?.name);
  const [roles, setRoles] = useState<any>(roleNameList.join(", "));
  const [firstName, setFirstName] = useState<string>(UserInfo?.firstName);
  const [lastName, setLastName] = useState<string>(UserInfo?.lastName);
  const [emailId, setEmailId] = useState<string>(UserInfo?.emailId);
  const [profilePic, setProfilePic] = useState<string>(editLogo);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [errorList, setErrorList] = useState<IErrorProps[]>([]);
  const [openToastError, setOpenToastError] = useState(false);
  const navigate = useNavigate();

  const { UserMgmt_Endpoints, Api, Version, UserController } =
    ConfigurationVariables;
  let usersUrl = `${UserMgmt_Endpoints}/${Api}/${Version}/${UserController}`;
  const rolesUrl = `${usersUrl}/roles`;
  const config = {
    headers: {
      accept: "application/json",
      customerId: "honeywell",
      Authorization: `Bearer ${getAccessToken()}`,
    },
  };
  let uploadProfilePicUrl = `${usersUrl}/userpic/${UserInfo?.id}`;

  const {
    isLoading: isFetchProfilePicLoading,
    data: profilePicData,
    error: fetchProfilePicError,
    statusCode: statusCodePic,
  } = useFetch(uploadProfilePicUrl, config);
  // istanbul ignore next

  const backButtonHandler = () => {
    navigate("/honeywell/user");
  };
  /**To get the user profile pic and show the preview */
  useEffect(() => {
    setProfilePic(profilePicData);
    setLoading(isFetchProfilePicLoading);
    if (isFetchProfilePicLoading === true) {
      setProfilePic(editLogo);
    }
    if (statusCodePic == 204) {
      setProfilePic(editLogo);
    }

    if (fetchProfilePicError) {
      const newErrorObj = {
        errorCode: fetchProfilePicError.response?.status,
        errorMessage: fetchProfilePicError.message,
      };
      // istanbul ignore next

      setErrorList((prevList) => [...prevList, newErrorObj]);
      // istanbul ignore next

      setOpenToastError(true);
    }
  }, [isFetchProfilePicLoading, profilePicData, fetchProfilePicError]);

  return (
    <>
      <Paper className="paper">
        <Grid container direction="row" p={1} className="grid-Heading">
          <Grid
            item
            xs={12}
            sx={{ paddingBottom: 1, display: "flex", flexDirection: "row" }}
          >
            <Button
              data-testid="arrow-back-icon"
              onClick={backButtonHandler}
              sx={{
                color: "#F0F0F0",
                padding: "0vh 0vh 0.7vh 0vh",
                fontSize: "1.1 rem",
                justifyContent: "start",
                minWidth: "1vw",
                minHeight: "0.5vh",
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: "0.9 rem" }} />
            </Button>

            <Typography className={classes.mainheading}>
              User Information
            </Typography>
          </Grid>
          <Grid
            container
            direction="column"
            sx={{
              paddingTop: "2vh",
              height: "32vh",
              minWidth: "850px",
              minHeight: "180px",
              width: "99%",
              background: "#272727",
              border: "1px solid #101010",
              boxShadow:
                "1px 1px 1px rgba(0, 0, 0, 0.15), 1px 1px 1px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Grid
              item
              sx={{
                width: "20%",
                height: "100%",
                minHeight: "150px",
                minWidth: "150px",
              }}
              data-testid="profile-pic"
            >
              {profilePic === editLogo ? (
                <UploadProfilePic
                  profilePic={profilePic}
                  isUploadDisabled={true}
                />
              ) : (
                <UploadProfilePic
                  profilePic={`data:image/jpeg;base64,${profilePic}`}
                  isUploadDisabled={true}
                />
              )}
            </Grid>
            <Grid
              container
              direction="row"
              sx={{
                width: "80%",
                height: "100%",
                minWidth: "600px",
                minHeight: "180px",
              }}
            >
              <Grid
                item
                sx={{
                  height: "10%",
                  width: "100%",
                }}
              >
                <Typography className={classes.secondaryHeading}>
                  BASIC INFORMATION
                </Typography>
              </Grid>

              <Grid
                container
                direction="row"
                sx={{ height: "40%", width: "100%" }}
              >
                <Grid item sx={{ width: "50%" }}>
                  <UserDisplayDetails label={"First Name"} detail={firstName} />
                </Grid>
                <Grid item sx={{ width: "50%" }}>
                  <UserDisplayDetails label={"Last Name"} detail={lastName} />
                </Grid>
              </Grid>

              <Grid item sx={{ height: "10%", width: "100%" }}>
                <Typography className={classes.secondaryHeading}>
                  OTHER INFORMATION
                </Typography>
              </Grid>
              <Grid
                container
                direction="row"
                sx={{ height: "40%", width: "100%" }}
              >
                <Grid item sx={{ width: "50%" }}>
                  <UserDisplayDetails label={"Email ID"} detail={emailId} />
                </Grid>
                <Grid item sx={{ width: "50%" }}>
                  <Grid
                    container
                    direction="column"
                    sx={{ padding: "1vh" }}
                    xs={12}
                    md={12}
                    xl={12}
                  >
                    {" "}
                    <FormLabel className={classes.labelHeading}>
                      Assigned Roles
                    </FormLabel>
                    <FormLabel
                      sx={{ paddingTop: "0.5vh", width: "100%" }}
                      className={classes.labelValue}
                    >
                      {roles}
                    </FormLabel>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      {isLoading && <Loader />}

      <ToastBarForError
        data-testid="my-component"
        open={openToastError}
        onClose={() => setOpenToastError(false)}
        errorList={errorList}
      />
    </>
  );
};

export default HoneywellUserInfo;
